import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  TextField,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Videocam as VideoIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { VideoUploadService } from '../../services/videoUploadService';

interface VideoPreviewPopupProps {
  open: boolean;
  onClose: () => void;
  onSend: (description: string, videoFile: File) => void;
  videoFile: File | null;
  loading: boolean;
  error: string | null;
}

const VideoPreviewPopup: React.FC<VideoPreviewPopupProps> = ({
  open,
  onClose,
  onSend,
  videoFile,
  loading,
  error,
}) => {
  const [description, setDescription] = useState('');
  const [videoMetadata, setVideoMetadata] = useState<{
    duration: number;
    width: number;
    height: number;
  } | null>(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [videoLoading, setVideoLoading] = useState(true);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(false);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);

  // Reset state when component opens
  useEffect(() => {
    if (open && videoFile) {
      setDescription('');
      setVideoMetadata(null);
      setPlaying(false);
      setMuted(true);
      setRetryCount(0);
      setVideoLoading(true);
      setVideoError(null);
      setCurrentTime(0);
      setDuration(0);
      setShowControls(false);
      loadVideoMetadata();
      
      // Fallback timeout to hide loading spinner after 2 seconds
      const loadingTimeout = setTimeout(() => {
        setVideoLoading(false);
      }, 2000);
      
      return () => clearTimeout(loadingTimeout);
    }
  }, [open, videoFile]);

  // Set up video event listeners
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let loadingCleared = false;

    const clearLoading = () => {
      if (!loadingCleared) {
        setVideoLoading(false);
        loadingCleared = true;
      }
    };

    const handlePlay = () => {
      console.log('Video started playing');
      setPlaying(true);
      setShowControls(true);
      clearLoading();
    };
    const handlePause = () => {
      console.log('Video paused');
      setPlaying(false);
      setShowControls(true);
    };
    const handleEnded = () => {
      console.log('Video ended');
      setPlaying(false);
      setShowControls(true);
    };
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };
    const handleLoadedMetadata = () => {
      console.log('Video metadata loaded, duration:', video.duration);
      clearLoading();
      setVideoError(null);
      setDuration(video.duration);
      // Video metadata is loaded, we can now get duration, width, height
      if (video.duration && video.videoWidth && video.videoHeight) {
        setVideoMetadata({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight,
        });
      }
    };
    const handleLoadedData = () => {
      console.log('Video data loaded, duration:', video.duration);
      if (video.duration && !duration) {
        setDuration(video.duration);
      }
    };
    const handleCanPlay = () => {
      clearLoading();
      setVideoError(null);
    };
    const handleError = () => {
      clearLoading();
      setVideoError('Failed to load video. Please try a different file.');
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('error', handleError);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('error', handleError);
    };
  }, [videoFile]);

  // Update duration when video metadata changes
  useEffect(() => {
    if (videoMetadata && videoMetadata.duration) {
      console.log('Setting duration from metadata:', videoMetadata.duration);
      setDuration(videoMetadata.duration);
    }
  }, [videoMetadata]);

  const loadVideoMetadata = async () => {
    if (!videoFile) return;

    try {
      const metadata = await VideoUploadService.getVideoMetadata(videoFile);
      setVideoMetadata(metadata);
    } catch (error) {
      console.error('Failed to load video metadata:', error);
    }
  };

  const handlePlayPause = async () => {
    const video = videoRef.current;
    if (!video) return;

    try {
      if (playing) {
        console.log('Pausing video');
        video.pause();
        // Don't set playing state here - let the pause event handle it
      } else {
        console.log('Playing video');
        // Hide loading spinner when user clicks play
        setVideoLoading(false);
        
        // Try to play the video
        await video.play();
        // Don't set playing state here - let the play event handle it
      }
    } catch (error) {
      console.error('Error playing/pausing video:', error);
      // If autoplay is blocked, show a message or handle gracefully
      if (error instanceof Error && error.name === 'NotAllowedError') {
        console.log('Autoplay blocked, user needs to interact first');
      }
    }
  };

  const handleMuteToggle = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !muted;
    setMuted(!muted);
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = parseFloat(event.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const showControlsTemporarily = () => {
    setShowControls(true);
    
    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    // Hide controls after 3 seconds of inactivity
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const handleSend = () => {
    if (!videoFile) return;
    onSend(description, videoFile);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    if (videoFile) {
      loadVideoMetadata();
    }
  };

  const formatFileSize = (bytes: number): string => {
    return VideoUploadService.formatFileSize(bytes);
  };

  const formatDuration = (seconds: number): string => {
    return VideoUploadService.formatDuration(seconds);
  };

  const getAspectRatio = (): string => {
    if (!videoMetadata) return '16:9';
    const ratio = videoMetadata.width / videoMetadata.height;
    if (ratio > 1.5) return '16:9';
    if (ratio > 1.3) return '4:3';
    if (ratio > 1.1) return '5:4';
    return '1:1';
  };

  if (!videoFile) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 2,
                background: 'linear-gradient(45deg, #ff6b6b 30%, #ee5a24 90%)',
                color: 'white',
              }}>
                <VideoIcon />
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold" color="white">
                  Preview Video
                </Typography>
                <Typography variant="body2" color="rgba(255,255,255,0.7)">
                  {videoFile.name}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} sx={{ 
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { 
                background: 'rgba(255,255,255,0.1)',
                transform: 'scale(1.1)'
              },
              transition: 'all 0.2s ease'
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0, background: '#000' }}>
          {error || videoError ? (
            <Box sx={{ p: 3 }}>
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  retryCount < 3 ? (
                    <Button color="inherit" size="small" onClick={handleRetry}>
                      Retry
                    </Button>
                  ) : null
                }
              >
                {error || videoError}
                {retryCount >= 3 && (
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    Maximum retry attempts reached. Please try a different video.
                  </Typography>
                )}
              </Alert>
            </Box>
          ) : (
            <Box>
              {/* Video Preview */}
              <Box sx={{ 
                position: 'relative',
                width: '100%',
                height: '400px',
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              onClick={handlePlayPause}
              onMouseEnter={showControlsTemporarily}
              onMouseMove={showControlsTemporarily}
              >
                <video
                  ref={videoRef}
                  src={URL.createObjectURL(videoFile)}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  preload="metadata"
                  muted={muted}
                  playsInline
                  controls={false}
                  onError={(e) => {
                    console.error('Video error:', e);
                    setVideoError('Failed to load video. Please try a different file.');
                    setVideoLoading(false);
                  }}
                />
                
                {/* Loading Indicator */}
                {videoLoading && (
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    color: 'white',
                  }}>
                    <CircularProgress size={40} sx={{ color: '#ff6b6b' }} />
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      Loading video...
                    </Typography>
                  </Box>
                )}
                
                {/* Play/Pause Overlay - Only show when not playing and not loading */}
                {!videoLoading && !playing && (
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    transition: 'opacity 0.3s ease',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.8)',
                    }
                  }}>
                    <PlayIcon sx={{ fontSize: 40 }} />
                  </Box>
                )}
                
                {/* Volume Control */}
                {!videoLoading && (
                  <Box sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                  }}>
                    <Tooltip title={muted ? "Unmute" : "Mute"}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMuteToggle();
                        }}
                        sx={{
                          color: 'white',
                          background: 'rgba(0, 0, 0, 0.7)',
                          '&:hover': {
                            background: 'rgba(0, 0, 0, 0.8)',
                          }
                        }}
                      >
                        {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Video Control Bar */}
                {!videoLoading && showControls && (
                  <Box sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.8))',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                    {/* Play/Pause Button */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayPause();
                        showControlsTemporarily();
                      }}
                      sx={{
                        color: 'white',
                        background: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                        }
                      }}
                    >
                      {playing ? <PauseIcon /> : <PlayIcon />}
                    </IconButton>

                    {/* Time Display */}
                    <Typography variant="body2" sx={{ color: 'white', minWidth: '80px' }}>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </Typography>
                    {/* Debug info - remove in production */}
                    {process.env.NODE_ENV === 'development' && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>
                        Debug: {currentTime.toFixed(1)}s / {duration.toFixed(1)}s
                      </Typography>
                    )}

                    {/* Progress Bar */}
                    <Box sx={{ flex: 1, mx: 1 }}>
                      <input
                        type="range"
                        min="0"
                        max={duration || 0}
                        step="0.1"
                        value={currentTime || 0}
                        onChange={handleSeek}
                        style={{
                          width: '100%',
                          height: '6px',
                          background: 'rgba(255, 255, 255, 0.3)',
                          outline: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          WebkitAppearance: 'none',
                          appearance: 'none',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onMouseUp={(e) => e.stopPropagation()}
                      />
                    </Box>

                    {/* Mute Button */}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMuteToggle();
                        showControlsTemporarily();
                      }}
                      sx={{
                        color: 'white',
                        background: 'rgba(255, 255, 255, 0.2)',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.3)',
                        }
                      }}
                    >
                      {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                    </IconButton>
                  </Box>
                )}
              </Box>

              {/* Video Information */}
              <Box sx={{ p: 3, background: 'rgba(0, 0, 0, 0.5)' }}>
                <Paper sx={{ 
                  p: 2, 
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                    <Chip
                      label={VideoUploadService.getVideoTypeDescription(videoFile.type)}
                      size="small"
                      sx={{ 
                        background: 'rgba(255, 107, 107, 0.2)',
                        color: '#ff6b6b',
                        border: '1px solid rgba(255, 107, 107, 0.3)'
                      }}
                    />
                    <Chip
                      label={formatFileSize(videoFile.size)}
                      size="small"
                      sx={{ 
                        background: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.2)'
                      }}
                    />
                    {videoMetadata && (
                      <>
                        <Chip
                          label={formatDuration(videoMetadata.duration)}
                          size="small"
                          sx={{ 
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        />
                        <Chip
                          label={`${videoMetadata.width}×${videoMetadata.height} (${getAspectRatio()})`}
                          size="small"
                          sx={{ 
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid rgba(255, 255, 255, 0.2)'
                          }}
                        />
                      </>
                    )}
                  </Box>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Add a description for your video..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#ff6b6b',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  />
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(255,255,255,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              color: 'white',
              borderColor: 'rgba(255,255,255,0.3)',
              '&:hover': {
                borderColor: 'rgba(255,255,255,0.5)',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            startIcon={<SendIcon />}
            disabled={loading}
            sx={{
              background: 'linear-gradient(45deg, #ff6b6b 30%, #ee5a24 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #ee5a24 30%, #ff6b6b 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.3)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            {loading ? 'Sending...' : 'Send Video'}
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default VideoPreviewPopup;
